import ComposeEmailFunction from '../ComposeEmailFunction';
import getAndPrefetchPersonaImageUri from './getAndPrefetchPersonaImageUri';
import getBrandUnsubscribeInfo from '../services/getBrandUnsubscribeInfo';
import makeMeetingActionCallbacks from './makeMeetingActionCallbacks';
import onPersonaUpdated from './onPersonaUpdated';
import sessionStore from 'owa-session-store/lib/store/store';
import unsubscribeFromBrand from '../services/unsubscribeFromBrand';
import { createOwsPersona } from '../dataLayer/createOwsPersona';
import { CustomData } from 'owa-analytics-actions';
import {
    getConfig,
    PersonaConfig,
    PersonaIdentifiers,
    ActionProperties,
    ComposeMailProperties,
} from '../personaConfig';
import { getNotesForPersona } from '../dataLayer/getNotesForPersona';
import { getOwsPersona } from '../dataLayer/getOwsPersona';
import { getOwsPersonaByEmailAddress } from '../dataLayer/getOwsPersonaByEmailAddress';
import { isFeatureEnabled } from 'owa-feature-flags';
import { logUsage } from 'owa-analytics';
import { getSessionId } from 'owa-analytics/lib/LazyAnalytics';
import { openMeeting, onMeetingInfoRequested } from 'owa-persona';
import { updateOwsPersona } from '../dataLayer/updateOwsPersona';
import {
    getLokiBootstrapperConfig,
    initializeLivePersonaCardAsync as initializeLivePersonaCard,
    initializeLoki,
    BootstrapperConfig,
} from 'owa-people-loki';
import { getMailToComposeDeeplink } from 'owa-popout/lib/utils/getMailComposeDeeplink';
import { DEPRECATED_openDeeplinkPopout } from 'owa-popout/lib/utils/DEPRECATED_openDeeplinkPopout';
import { getMailReadDeeplink } from 'owa-popout/lib/utils/getMailReadDeeplink';
import { default as getIsConsumer } from 'owa-session-store/lib/utils/isConsumer';

let initializeLivePersonaCardPromise: Promise<void>;

export default function initializeLivePersonaCardAsync(
    composeEmailOverride?: ComposeEmailFunction
): Promise<void> {
    const config = getConfig();

    initializeLivePersonaCardPromise =
        initializeLivePersonaCardPromise ||
        initializeLivePersonaCard(makeLivePersonaCardConfig(config, composeEmailOverride));

    return initializeLivePersonaCardPromise;
}

function makeLivePersonaCardConfig(
    config: PersonaConfig,
    composeEmailOverride?: ComposeEmailFunction
): BootstrapperConfig {
    // TODO(jdferre): we should define types for action, data and privateData callbacks.
    // We should also update their definitions, or maybe get a package from LPC with them?
    const actionCallbacks: any = makeActionCallbacks(config, composeEmailOverride);
    const dataCallbacks: any = makeDataCallbacks(config);
    const privateDataCallbacks: any = makePrivateDataCallbacks();
    const dataUpdateBroadcaster: any = makeDataUpdateBroadcaster(config);

    // We need to initialize Loki's configuration before obtaining the BootstrapperConfig
    initializeLokiConfiguration();

    const bootstrapperConfig = getLokiBootstrapperConfig();
    const timeFormat: string = sessionStore.userConfiguration.UserOptions.TimeFormat;
    const dateFormat: string = sessionStore.userConfiguration.UserOptions.DateFormat;
    const is24HourFormat: boolean = timeFormat && timeFormat.indexOf('tt') === -1;

    return {
        ...bootstrapperConfig,
        actionCallbacks: actionCallbacks,
        dataCallbacks: dataCallbacks,
        privateDataCallbacks: privateDataCallbacks,
        dataUpdateBroadcaster: dataUpdateBroadcaster,
        options: {
            isLinkedInIntegrationEnabled: false,
            timeFormat: timeFormat,
            dateFormat: dateFormat,
            is24HourFormat: is24HourFormat,
        },
    };
}

function makeActionCallbacks(
    config: PersonaConfig,
    composeEmailOverride?: ComposeEmailFunction
): any {
    const actionCallbacks: any = {
        readMail: readMail,
        composeMail: makeComposeMailCallback(config, composeEmailOverride),
        meetingActionCallbacks: makeMeetingActionCallbacks(config),
        onPersonaUpdated: onPersonaUpdated,
        openMeeting: onOpenMeeting,
    };

    if (isFeatureEnabled('rp-brandCardsUnsubscribe')) {
        actionCallbacks.unsubscribeFromBrand = unsubscribeFromBrand;
    }

    if (config.actionCallbacks) {
        if (config.actionCallbacks.favoritePersona) {
            actionCallbacks.favoritePersona = config.actionCallbacks.favoritePersona;
        }

        if (config.actionCallbacks.unfavoritePersona) {
            actionCallbacks.unfavoritePersona = config.actionCallbacks.unfavoritePersona;
        }

        if (config.actionCallbacks.readMail) {
            actionCallbacks.readMail = config.actionCallbacks.readMail;
        }

        if (config.actionCallbacks.toggleSubscribeToGroup) {
            actionCallbacks.toggleSubscribeToGroup = config.actionCallbacks.toggleSubscribeToGroup;
        }
        if (config.actionCallbacks.editGroup) {
            actionCallbacks.editGroup = config.actionCallbacks.editGroup;
        }
        if (config.actionCallbacks.addGroupMembers) {
            actionCallbacks.addGroupMembers = config.actionCallbacks.addGroupMembers;
        }
        if (config.actionCallbacks.updateMembership) {
            actionCallbacks.updateMembership = config.actionCallbacks.updateMembership;
        }
    }

    return actionCallbacks;
}

function makeDataCallbacks(config: PersonaConfig): any {
    const dataCallbacks: any = {
        getAndPrefetchPersonaImageUri: getAndPrefetchPersonaImageUri,
        getPersonaInfo: getPersonaInfo,
    };

    if (config.dataCallbacks && config.dataCallbacks.getIsPersonaFavorited) {
        dataCallbacks.getIsPersonaFavorited = config.dataCallbacks.getIsPersonaFavorited;
    }

    if (isFeatureEnabled('rp-brandCardsUnsubscribe')) {
        dataCallbacks.getBrandUnsubscribeInfo = getBrandUnsubscribeInfo;
    }

    return dataCallbacks;
}

function makePrivateDataCallbacks(): any {
    return {
        updateOwsPersona,
        getOwsPersona,
        getOwsPersonaByEmailAddress,
        getNotesForPersona,
        createOwsPersona,
    };
}

function makeDataUpdateBroadcaster(config: PersonaConfig): any {
    const dataUpdateBroadcaster: any = {};

    if (!config.dataUpdateBroadcaster) {
        return dataUpdateBroadcaster;
    }

    if (config.dataUpdateBroadcaster.setGroupUpdateListener) {
        dataUpdateBroadcaster.setGroupUpdateListener =
            config.dataUpdateBroadcaster.setGroupUpdateListener;
    }

    if (config.dataUpdateBroadcaster.setGroupMembersUpdateListener) {
        dataUpdateBroadcaster.setGroupMembersUpdateListener =
            config.dataUpdateBroadcaster.setGroupMembersUpdateListener;
    }

    if (config.dataUpdateBroadcaster.setGroupDeleteListener) {
        dataUpdateBroadcaster.setGroupDeleteListener =
            config.dataUpdateBroadcaster.setGroupDeleteListener;
    }

    if (config.dataUpdateBroadcaster.updatePopupStateListener) {
        dataUpdateBroadcaster.updatePopupStateListener =
            config.dataUpdateBroadcaster.updatePopupStateListener;
    }

    return dataUpdateBroadcaster;
}

function initializeLokiConfiguration(): void {
    const isConsumer: boolean = getIsConsumer();
    const culture: string = sessionStore.userConfiguration.SessionSettings.UserCulture;
    const tenantId: string = sessionStore.userConfiguration.SessionSettings.TenantGuid;
    const clientCorrelationId = getSessionId();
    const useDogfood: boolean = isFeatureEnabled('peo-lokiDogfood');
    const logger = (eventName: string, customData: CustomData) => {
        logUsage(eventName, customData);
    };

    initializeLoki({
        culture,
        isConsumer,
        useDogfood,
        logger,
        tenantId,
        clientCorrelationId,
    });
}

function readMail(mailId: string): void {
    DEPRECATED_openDeeplinkPopout(getMailReadDeeplink(mailId), /*isExternalApp*/ true);
}

function makeComposeMailCallback(
    config: PersonaConfig,
    composeEmailOverride?: ComposeEmailFunction
): (recipient: string, subject?: string, body?: string) => void {
    return (
        recipient: string,
        subject?: string,
        body?: string,
        properties?: ActionProperties & ComposeMailProperties,
        personaId?: PersonaIdentifiers
    ) => {
        if (composeEmailOverride) {
            composeEmailOverride(recipient, recipient);
        } else if (config.actionCallbacks && config.actionCallbacks.composeMail) {
            config.actionCallbacks.composeMail(recipient, subject, body);
        } else {
            DEPRECATED_openDeeplinkPopout(
                getMailToComposeDeeplink({
                    to: {
                        smtpAddress: recipient,
                        displayName: getComposeRecipientDisplayName(properties),
                    },
                }),
                /*isExternalApp*/ true
            );
        }

        // Compose view is triggered in existing window, therefore dismiss card so it's not left hanging without an anchor
        (window as any).LPC.dismissCard();
    };
}

function getComposeRecipientDisplayName(
    properties: ActionProperties & ComposeMailProperties
): string | undefined {
    if (properties) {
        if (properties.personaDisplayName) {
            return properties.personaDisplayName;
        }

        if (properties.recipientDisplayName) {
            return properties.recipientDisplayName;
        }
    }

    return undefined;
}

function getPersonaInfo(
    personaId: PersonaIdentifiers,
    successCallback: (persona: any, errorMessage: string) => void
): void {
    if (personaId.PersonaType === 'Meeting') {
        onMeetingInfoRequested(personaId, successCallback);
    }
}

function onOpenMeeting(meetingId: string) {
    // Calendar forms are triggered in existing window, therefore dismiss card so it's not left hanging without an anchor
    (window as any).LPC.dismissCard();
    openMeeting(meetingId);
}
